// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { CONST_MONTH, TABLEAU_COLORS } from '../../../shared/const/constants';
import { DwmDataSPModelsComplaintChartsComplaintsModelCharts1, DwmDataSPModelsComplaintChartsComplaintsModelCharts2, DwmDataSPModelsComplaintChartsComplaintsModelCharts4 } from '../../../shared/generated';
import { ChartDataModel } from '../../../shared/models/chart-data.model';
import { setBackgroundColorForLabels } from '../../../shared/utils/util-methods';

interface BaseChartData {
  year: string | number;
  month?: number;
  count: number;
  [key: string]: any;
}

const sortDates = (date1: string, date2: string) =>
  new Date(date1).setHours(0, 0, 0, 0) - new Date(date2).setHours(0, 0, 0, 0);

const createDatasets = (prepData: any[], colorStartIndex: number = 6, useLineStyle: boolean = false) => {
  if (!prepData[0]) return [];

  let colorCounter = colorStartIndex;
  return Object.keys(prepData[0]).map((label) => {
    let backgroundColorIndex = setBackgroundColorForLabels(label);
    backgroundColorIndex ??= colorCounter++;
    const backgroundColor = TABLEAU_COLORS[backgroundColorIndex];

    return {
      label,
      data: prepData.map((item) => item[label]),
      backgroundColor,
      ...(useLineStyle && {
        borderColor: TABLEAU_COLORS[colorCounter],
        pointRadius: 3,
      }),
    };
  });
};

const createDatasetsCycled = (prepData: any[], useLineStyle: boolean = false) => {
  if (!prepData[0]) return [];
  let colorCounter = 0;
  return Object.keys(prepData[0]).map((label) => {
    if (colorCounter === TABLEAU_COLORS.length) colorCounter = 0;
    const color = TABLEAU_COLORS[colorCounter++];
    return {
      label,
      data: prepData.map((item) => item[label]),
      backgroundColor: color,
      ...(useLineStyle && { borderColor: color, pointRadius: 3 }),
    };
  });
};

const prepareYearLabels = (data: BaseChartData[]) => {
  const labelsPrep = data.reduce((acc, curr) => {
    acc[curr.year] = acc[curr.year] || [];
    acc[curr.year].push({});
    return acc;
  }, {} as Record<string, any[]>);

  const labels = Object.keys(labelsPrep);
  labels.sort(sortDates);
  return labels;
};

const prepareMonthLabels = (data: BaseChartData[]) => {
  const labelsResult = data.reduce((acc, curr) => {
    const key = `${CONST_MONTH[curr.month - 1]}/${curr.year}`;
    acc[key] = [];
    return acc;
  }, {} as Record<string, any[]>);

  const labels = Object.keys(labelsResult);
  labels.sort(sortDates);
  return labels;
};

const prepareLabels = (data: any[], key: string): string[] =>
  [...new Set(data.map(item => String(item[key])))];

const prepareChartDataByCategory = (
  data: BaseChartData[],
  labels: string[],
  categoryKey: string,
  dateKey: string,
  valueKey: string = 'count'
) => {
  const result = data.reduce((acc, curr) => {
    const category = curr[categoryKey];
    acc[category] = acc[category] || [];
    acc[category].push({
      [dateKey]: curr[dateKey],
      value: curr[valueKey],
    });
    return acc;
  }, {} as Record<string, any[]>);

  const prepData = labels.map((label) => {
    const tempData = {};
    Object.keys(result).forEach((category) => {
      const matchingItem = result[category].find(item => String(item[dateKey]) === String(label));
      tempData[category] = matchingItem ? matchingItem.value : 0;
    });
    return tempData;
  });

  return prepData;
};

const addDateField = (payload: any[]) =>
  payload.map(item => ({ ...item, date: `${CONST_MONTH[item.month - 1]}/${item.year}` }));

const getYearDatasetMethod = (payload: any[], categoryKey: string): ChartDataModel => {
  const labels = prepareYearLabels(payload);
  const prepData = prepareChartDataByCategory(payload, labels, categoryKey, 'year');
  return { labels, datasets: createDatasets(prepData) };
};

const getMonthDatasetMethod = (payload: any[], categoryKey: string): ChartDataModel => {
  const labels = prepareMonthLabels(payload);
  const prepData = prepareChartDataByCategory(addDateField(payload), labels, categoryKey, 'date');
  return { labels, datasets: createDatasets(prepData, 6, true) };
};

export const getChartCustomerDecisionPerShipmentYearDataset = (payload: DwmDataSPModelsComplaintChartsComplaintsModelCharts1[]): ChartDataModel =>
  getYearDatasetMethod(payload, 'decisionClient');

export const getChartCustomerDecisionPerShipmentMonthDataset = (payload: DwmDataSPModelsComplaintChartsComplaintsModelCharts2[]): ChartDataModel =>
  getMonthDatasetMethod(payload, 'decisionClient');

export const getChartSupplierFeedbackPerShipmentYearDataset = (payload: DwmDataSPModelsComplaintChartsComplaintsModelCharts3[]): ChartDataModel =>
  getYearDatasetMethod(payload, 'decisionSupplier');

export const getChartSupplierFeedbackPerShipmentMonthDataset = (payload: DwmDataSPModelsComplaintChartsComplaintsModelCharts4[]): ChartDataModel =>
  getMonthDatasetMethod(payload, 'decisionSupplier');

export const getChartSupplierFeedbackPerRepairYearDataset = (payload: DwmDataSPModelsComplaintChartsComplaintsModelCharts5[]): ChartDataModel =>
  getYearDatasetMethod(payload, 'decisionSupplier');

export const getChartSupplierFeedbackPerRepairMonthDataset = (payload: DwmDataSPModelsComplaintChartsComplaintsModelCharts6[]): ChartDataModel =>
  getMonthDatasetMethod(payload, 'decisionSupplier');

export const getChartSupplierFeedbackPerPartProductionYearDataset = (payload: DwmDataSPModelsComplaintChartsComplaintsModelCharts7[]): ChartDataModel =>
  getYearDatasetMethod(payload, 'decisionSupplier');

export const getChartSupplierFeedbackPerPartProductionMonthDataset = (payload: DwmDataSPModelsComplaintChartsComplaintsModelCharts8[]): ChartDataModel =>
  getMonthDatasetMethod(payload, 'decisionSupplier');

export const getChartCustomerDecisionPerPartProductionYearDataset = (payload: DwmDataSPModelsComplaintChartsComplaintsModelCharts9[]): ChartDataModel =>
  getYearDatasetMethod(payload, 'decisionClient');

export const getChartCustomerDecisionPerPartProductionMonthDataset = (payload: DwmDataSPModelsComplaintChartsComplaintsModelCharts10[]): ChartDataModel =>
  getMonthDatasetMethod(payload, 'decisionClient');

export const getChartEightDNamePerPartProductionMonthDataset = (payload: DwmDataSPModelsComplaintChartsComplaintsModelCharts11[]): ChartDataModel => {
  const labels = prepareMonthLabels(payload);
  const prepData = prepareChartDataByCategory(addDateField(payload), labels, 'name8D', 'date');
  return { labels, datasets: createDatasetsCycled(prepData, true) };
};

export const getChartPartProductionYearPerSupplierNumberDataset = (payload: DwmDataSPModelsComplaintChartsComplaintsModelCharts12[]): ChartDataModel => {
  const labels = prepareLabels(payload, 'supplierNumber');
  const prepData = prepareChartDataByCategory(payload, labels, 'year', 'supplierNumber');
  return { labels, datasets: createDatasetsCycled(prepData) };
};

export const getChartRepairCountryPerSupplierNumberDataset = (payload: DwmDataSPModelsComplaintChartsComplaintsModelCharts13[]): ChartDataModel => {
  const labels = prepareLabels(payload, 'supplierNumber');
  const prepData = prepareChartDataByCategory(payload, labels, 'return_CountryCalc', 'supplierNumber');
  return { labels, datasets: createDatasetsCycled(prepData) };
};

export const getChartCustomerDecisionPerVehicleModelDataset = (payload: DwmDataSPModelsComplaintChartsComplaintsModelCharts14[]): ChartDataModel => {
  const labels = prepareLabels(payload, 'vehicleModel');
  const prepData = prepareChartDataByCategory(payload, labels, 'decisionClient', 'vehicleModel');
  return { labels, datasets: createDatasets(prepData) };
};

export const getChartCustomerDecisionPerReturnCountryDataset = (payload: DwmDataSPModelsComplaintChartsComplaintsModelCharts15[]): ChartDataModel => {
  const labels = prepareLabels(payload, 'returnCountryCalc');
  const prepData = prepareChartDataByCategory(payload, labels, 'decisionClient', 'returnCountryCalc');
  return { labels, datasets: createDatasets(prepData) };
};
